import React from 'react';
import { motion } from 'motion/react';
import { MessageCircle, ArrowRight, Users, Bug, Sparkles, TestTube, Megaphone, Palette } from 'lucide-react';
import { cn } from '@/lib/utils';

const DISCORD_URL = 'https://discord.gg/cdwNFdbx';

interface DiscordCTAProps {
  variant?: 'card' | 'banner' | 'inline';
  className?: string;
}

const benefits = [
  { icon: TestTube, text: 'Become a beta tester' },
  { icon: Bug, text: 'Report bugs directly' },
  { icon: Sparkles, text: 'Suggest new features' },
  { icon: Palette, text: 'Share your creations' },
  { icon: Megaphone, text: 'Get early access to updates' },
  { icon: Users, text: 'Connect with other creators' },
];

export const DiscordCTA: React.FC<DiscordCTAProps> = ({ variant = 'card', className }) => {
  if (variant === 'inline') {
    return (
      <a
        href={DISCORD_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/20 text-[#5865F2] font-bold text-xs uppercase tracking-widest hover:bg-[#5865F2] hover:text-white transition-all duration-300',
          className
        )}
      >
        <MessageCircle className="w-4 h-4" />
        Join Discord Community
      </a>
    );
  }

  if (variant === 'banner') {
    return (
      <section className={cn('py-12 px-6', className)}>
        <div className="max-w-4xl mx-auto">
          <motion.a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.01 }}
            className="group flex flex-col sm:flex-row items-center justify-between gap-6 p-8 rounded-[2rem] bg-gradient-to-r from-[#5865F2] to-[#7B68EE] text-white border border-white/10 shadow-xl shadow-[#5865F2]/20 hover:shadow-2xl hover:shadow-[#5865F2]/30 transition-all duration-300"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold font-display uppercase tracking-tight">Join Our Discord</h3>
                <p className="text-white/70 text-sm font-medium">Beta testing, bug reports, feature requests & creator discussions</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest bg-white/20 px-6 py-3 rounded-xl border border-white/20 group-hover:bg-white group-hover:text-[#5865F2] transition-all">
              Join Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </motion.a>
        </div>
      </section>
    );
  }

  // Default: card variant
  return (
    <section className={cn('py-20 px-6', className)}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#5865F2] via-[#6C70F0] to-[#7B68EE] p-10 md:p-14 text-white shadow-2xl shadow-[#5865F2]/20"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-black/5 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2 pointer-events-none" />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-white/60">Community</div>
            </div>

            <h3 className="text-3xl md:text-4xl font-bold font-display uppercase tracking-tighter mb-4">
              Join the Fun Circle Games Community
            </h3>
            <p className="text-white/70 text-base mb-10 max-w-2xl font-medium leading-relaxed">
              Connect with thousands of creators, get early access to features, and help shape the future of physics content creation.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {benefits.map((benefit) => (
                <div key={benefit.text} className="flex items-center gap-3 text-white/80 text-sm font-medium">
                  <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-4 h-4" />
                  </div>
                  {benefit.text}
                </div>
              ))}
            </div>

            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-[#5865F2] rounded-xl font-black text-xs uppercase tracking-widest hover:bg-white/90 hover:scale-105 active:scale-95 transition-all duration-300 shadow-xl shadow-black/10"
            >
              <MessageCircle className="w-4 h-4" />
              Join Discord Community
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DiscordCTA;
